import { list } from '@/core/repo';
import { getSetting, setSetting, useSetting } from '@/core/settings';
import type { SleepSession } from '@/core/types';
import { peekWorldCache } from '@/modules/activity/trace';
import { getProfile } from '@/modules/health/profile';
import { buildNights } from '@/modules/sleep/model';
import { PLACES_KEY, peekTrips } from './trips';

// Orbit (time tracking) gets each night's sleep and every workout, so they show as time spent.
// Orbit shows a pairing code (Settings → Integrations → Circa); here it's traded for a token,
// and from then on this app sends: both apps are end-to-end encrypted, so it can't be one
// server reading the other. Each night goes with the zone Circa worked out for it (what you
// said, Trace, Trips App…), and goes again when that changes; Orbit updates it in place.

export const ORBIT_KEY = 'connections.orbit';
export const ORBIT_ORIGIN = 'https://orbitnote.app';
/** The first send has every night ever; later ones resend the last few weeks. */
const AGAIN_DAYS = 21;
const CHUNK = 500;

export interface OrbitLink {
  origin: string;
  token: string;
  connectedAt: number;
  lastSync?: number;
  error?: string;
}

interface OrbitItem {
  id: string;
  kind: 'sleep' | 'nap' | 'workout';
  start: number;
  end: number;
  tz?: string;
  title: string;
  note?: string;
  deleted?: boolean;
}

export const getOrbitLink = () => getSetting<OrbitLink | null>(ORBIT_KEY, null);
export const useOrbitLink = () => useSetting<OrbitLink | null>(ORBIT_KEY, null)[0];

function clean(origin: string): string {
  const o = origin.trim().replace(/\/+$/, '');
  return /^https?:\/\//.test(o) ? o : `https://${o}`;
}

async function readError(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { error?: string };
    if (j.error) return j.error;
  } catch {}
  return `Orbit answered ${res.status}`;
}

/** Pairs with the code Orbit shows, then sends every night and workout there is. */
export async function connectOrbit(code: string, origin = ORBIT_ORIGIN): Promise<number> {
  const base = clean(origin);
  let res: Response;
  try {
    res = await fetch(`${base}/api/circa/claim`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code: code.trim() }),
    });
  } catch {
    throw new Error(`Can’t reach Orbit at ${base}. Check the address and your connection.`);
  }
  if (!res.ok) throw new Error(await readError(res));
  const { token } = (await res.json()) as { token: string };
  setSetting(ORBIT_KEY, { origin: base, token, connectedAt: Date.now() } satisfies OrbitLink);
  return syncOrbit({ all: true });
}

export function disconnectOrbit() {
  setSetting(ORBIT_KEY, null);
}

const hm = (s: number) => {
  const m = Math.round(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ''}`.trim() : `${m}m`;
};

/**
 * A fixed-offset zone ("UTC-5" -> "Etc/GMT+5": Etc names count the other way) for a night only
 * Oura's clock places. Still worth sending: it's where your phone was, and Orbit uses nights to
 * tell where you were for anything else that day. Half-hour offsets have no such zone.
 */
function offsetZone(min: number): string | undefined {
  if (min % 60 !== 0 || Math.abs(min) > 14 * 60) return undefined;
  const h = min / 60;
  return h === 0 ? 'Etc/UTC' : `Etc/GMT${h > 0 ? '-' : '+'}${Math.abs(h)}`;
}

const sessionItem = (s: SleepSession, main: boolean, tz: string | null): OrbitItem => {
  const nap = !main && s.type !== 'long_sleep';
  return {
    id: `sleep-${s.id}`,
    kind: nap ? 'nap' : 'sleep',
    start: s.start,
    end: s.end,
    ...(tz ? { tz } : {}),
    title: nap ? 'Nap' : 'Sleep',
    ...(s.totalS ? { note: `${hm(s.totalS)} asleep` } : {}),
  };
};

/** What to send: nights (sleep and naps, with their zone) and workouts since `since`. */
function itemsSince(since: number): OrbitItem[] {
  const sessions = list('sleep');
  const { nights } = buildNights(
    sessions,
    {
      trace: peekWorldCache(),
      trips: peekTrips(),
      told: getSetting<Record<string, string>>(PLACES_KEY, {}),
    },
    getProfile().homeZone,
  );
  const out: OrbitItem[] = [];
  const sent = new Set<string>();
  for (const n of nights) {
    if (n.main.end < since) continue;
    // Where Circa placed the night; else Oura's own clock, when the ring recorded one.
    const ouraClock = n.main.endOffset !== undefined || n.main.startOffset !== undefined;
    const tz = n.clock.zone ?? (ouraClock ? (offsetZone(n.clock.endOff) ?? null) : null);
    for (const [s, main] of [
      [n.main, true] as const,
      ...n.others.map((o) => [o, false] as const),
    ]) {
      const it = sessionItem(s, main, tz);
      sent.add(it.id);
      out.push(it);
    }
  }
  // Deleted here (or rest periods, which aren't sleep): gone from Orbit too.
  for (const s of sessions)
    if (s.end >= since && !sent.has(`sleep-${s.id}`) && (s.deleted || s.type === 'rest'))
      out.push({ ...sessionItem(s, false, null), deleted: true });
  for (const w of list('workout')) {
    if (w.start < since) continue;
    const id = `workout-${w.id}`;
    if (w.deleted || !(w.durationS > 0)) {
      out.push({
        id,
        kind: 'workout',
        start: w.start,
        end: w.start + 1,
        title: w.type,
        deleted: true,
      });
      continue;
    }
    out.push({
      id,
      kind: 'workout',
      start: w.start,
      end: w.start + Math.round(w.durationS * 1000),
      title: w.type || 'Workout',
      ...(w.distanceM ? { note: `${(w.distanceM / 1609.344).toFixed(1)} mi` } : {}),
    });
  }
  return out;
}

/** Sends sleep and workouts to Orbit. Resolves with how many went. */
export async function syncOrbit({ all = false } = {}): Promise<number> {
  const link = getOrbitLink();
  if (!link) return 0;
  const items = itemsSince(all || !link.lastSync ? 0 : Date.now() - AGAIN_DAYS * 86_400_000);
  try {
    for (let i = 0; i < items.length; i += CHUNK) {
      const res = await fetch(`${link.origin}/api/circa/push`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${link.token}` },
        body: JSON.stringify({ items: items.slice(i, i + CHUNK) }),
      });
      if (!res.ok) throw new Error(await readError(res));
    }
  } catch (err) {
    const msg = err instanceof TypeError ? 'Can’t reach Orbit right now' : (err as Error).message;
    setSetting(ORBIT_KEY, { ...link, error: msg } satisfies OrbitLink);
    throw new Error(msg);
  }
  setSetting(ORBIT_KEY, { ...link, lastSync: Date.now(), error: undefined } satisfies OrbitLink);
  return items.length;
}
